import { useState } from "react";
import ReservationModel from "../../models/Reservations";
import StatusBadge from "./StatusBadge";

const LoadingWheel = () => {
    return (
        <div className="">
            <svg aria-hidden="true" className="mr-2 w-4 h-4 text-gray-200 animate-spin dark:text-gray-300 fill-white" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
            </svg>
        </div>
    )
}

interface IProps {
    reservation: ReservationModel;
    closeDetails: () => void;
}

const BookingDetails = (props: IProps) => {
    const [validateLoading, setValidateLoading] = useState<boolean>(false);
    const [cancelLoading, setCancelLoading] = useState<boolean>(false);

    const cancelReservation = async () => {
        const start = new Date().getTime();
        setCancelLoading(true);
        await props.reservation.cancel();

        const end = new Date().getTime();
        const time = end - start;
        if (time < 1000) {
            setTimeout(() => {
                setCancelLoading(false);
            }, 1000 - time);
        } else {
            setCancelLoading(false);
        }
    }

    const validateReservation = async () => {
        const start = new Date().getTime();
        setValidateLoading(true);
        await props.reservation.validate();

        const end = new Date().getTime();
        const time = end - start;
        if (time < 1000) {
            setTimeout(() => {
                setValidateLoading(false);
            }, 1000 - time);
        } else {
            setValidateLoading(false);
        }
    }

    return (
        <div className="flex flex-col gap-8 w-fit">
            <button onClick={props.closeDetails} className="flex items-center gap-2 w-fit hover:bg-[#EDECFF] pr-2">
                <div className="bg-[#EDECFF] flex justify-center items-center p-1">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                </div>
                <p className="text-xl">Retour</p>
            </button>

            <div className="flex items-center">
                <div className="text-3xl">{`${props.reservation.houseName()}`}</div>
                <p className="text-3xl italic px-4">-</p>
                <div className="text-3xl italic text-gray-500">{`Du ${props.reservation.arrival.toLocaleString()} au ${props.reservation.departure.toLocaleString()}`}</div>
                <div className="pl-8 flex pt-1"><StatusBadge res={props.reservation} /></div>
            </div>
            <div className="w-full flex flex-col gap-4 text-lg">
                <p className="font-bold text-xl">Commande</p>

                <div className="flex flex-col md:flex-row gap-2 justify-between">
                    <p>Numéro de commande</p>
                    <p>{props.reservation._id || "-"}</p>
                </div>

                <div className="flex flex-col md:flex-row gap-2 justify-between">
                    <p>Est un cadeau</p>
                    <p>{props.reservation.hasGift ? "Oui" : "Non"}</p>
                </div>

                <div className="flex flex-col md:flex-row gap-2 justify-between">
                    <p>Code promo</p>
                    <p>{props.reservation.promocode || "-"}</p>
                </div>

                <div className="flex flex-col md:flex-row gap-2 justify-between">
                    <p>Total commande</p>
                    <p>{props.reservation.totalPrice ?? "-"} €</p>
                </div>
            </div>

            <div className="my-8 flex flex-col md:flex-row gap-12 w-full">

                <div className="md:w-1/2 flex flex-col gap-4 text-lg">

                    <p className="font-bold text-xl">Voyageurs</p>

                    <div className="flex flex-col md:flex-row gap-2 justify-between">
                        <p>Adultes</p>
                        <p>{props.reservation.adults || "0"}</p>
                    </div>
                    <div className="flex flex-col md:flex-row gap-2 justify-between">
                        <p>Enfants</p>
                        <p>{props.reservation.children || "0"}</p>
                    </div>
                    <div className="flex flex-col md:flex-row gap-2 justify-between">
                        <p>Bébés</p>
                        <p>{props.reservation.babies || "0"}</p>
                    </div>
                    <div className="flex flex-col md:flex-row gap-2 justify-between">
                        <p>Animaux</p>
                        <p>{props.reservation.animals || "0"}</p>
                    </div>
                </div>

                <div className="md:w-1/2 flex flex-col gap-4 text-lg">

                    <p className="font-bold text-xl">Contact</p>

                    <div className="flex flex-col md:flex-row gap-2 justify-between">
                        <p>Name</p>
                        <p>{props.reservation.contact.name || "-"}</p>
                    </div>
                    <div className="flex flex-col md:flex-row gap-2 justify-between">
                        <p>Email</p>
                        <p>{props.reservation.contact.email || "-"}</p>
                    </div>
                    <div className="flex flex-col md:flex-row gap-2 justify-between">
                        <p>Phone</p>
                        <p>{props.reservation.contact.phone || "-"}</p>
                    </div>
                    <div className="flex flex-col md:flex-row gap-2 justify-between">
                        <p>Commentaire</p>
                        <p>{props.reservation.comments || "-"}</p>
                    </div>
                </div>
            </div>

            <div className="flex justify-end">
                {
                    !props.reservation.cancelled && <button className="flex items-center mx-1 px-8 py-2 bg-[#79819b] text-white font-bold rounded-md" onClick={() => cancelReservation()}>{cancelLoading && <LoadingWheel />}Annuler la Réservation</button>
                }
                {
                    !props.reservation.validated && <button className="flex items-center mx-1 px-8 py-2 bg-[#515a75] text-white font-bold rounded-md" onClick={() => validateReservation()}>{validateLoading && <LoadingWheel />}Valider la Réservation</button>
                }
            </div>
        </div>
    )
}

export default BookingDetails;